import { defineStore } from 'pinia';
import useAccountsApi from '~/composables/api/useAccountsApi';
import { AccountDetailsTabs } from '~/enums/accounts/account-details-tabs.enum';
import { toPascalCaseName } from '~/utils/helper';
import { hasAcceptedOnboarding } from '~/utils/account-onboarding';
import type { AccountStatus } from '~/enums/accounts/account-status.enum';
import type {
  AccountModel,
  IdentityModel,
  ProfileModel,
  DocumentModel,
  AccountRequirementModel,
  AccountRestrictionModel,
  WalletModel
} from '~/types';

export const useAccountDetailsStore = defineStore('account-details', {
  state: () => ({
    id: '' as string,
    identity: {} as IdentityModel,
    status: '' as AccountStatus,
    accountBalance: null as number | null,
    type: '' as string,
    documents: [] as DocumentModel[],
    profiles: [] as ProfileModel[],
    requirements: [] as AccountRequirementModel[],
    restrictions: [] as AccountRestrictionModel[],
    selectedProfile: {} as ProfileModel,
    selectedRequirement: {} as AccountRequirementModel,
    OnboardingId: '' as string | undefined,
    isLoading: false as boolean,
    hasComplianceReport: false as boolean,
    activeTab: AccountDetailsTabs.Overview as AccountDetailsTabs,
    selectedDocument: null as DocumentModel | null,
    activeDocumentTab: ['company'] as string[],
    selectedWallet: null as WalletModel | null
  }),
  getters: {
    accountDisplayName: (state) => {
      const name = state.identity.Name?.trim?.();
      if (name) {
        return toPascalCaseName(name);
      }
      const firstName = state.identity.FirstName ? toPascalCaseName(state.identity.FirstName) : '';
      const lastName = state.identity.LastName ? toPascalCaseName(state.identity.LastName) : '';

      return `${firstName} ${lastName}`.trim();
    },

    hasAcceptedOnboarding: (state) => hasAcceptedOnboarding(state.profiles),

    isTabAccessRestricted(): boolean {
      return !this.hasAcceptedOnboarding;
    },

    canAccessDocumentsTab(): boolean {
      return this.documents.length > 0;
    }
  },
  actions: {
    resetAccountDetails() {
      this.id = '';
      this.identity = {} as IdentityModel;
      this.status = '' as AccountStatus;
      this.accountBalance = null;
      this.type = '';
      this.documents = [];
      this.profiles = [];
      this.requirements = [];
      this.restrictions = [];
      this.selectedProfile = {} as ProfileModel;
      this.selectedRequirement = {} as AccountRequirementModel;
      this.OnboardingId = undefined;
      this.hasComplianceReport = false;
      this.selectedDocument = null;
      this.activeDocumentTab = ['company'];
      this.selectedWallet = null;
    },

    normalizeTab(tab?: string | string[]) {
      const normalizedTab = Array.isArray(tab) ? tab[0] : tab;

      return Object.values(AccountDetailsTabs).includes(normalizedTab as AccountDetailsTabs)
        ? (normalizedTab as AccountDetailsTabs)
        : AccountDetailsTabs.Overview;
    },

    setActiveTab(tab?: string | string[]) {
      this.activeTab = this.normalizeTab(tab);

      return this.activeTab;
    },

    async initAccountDetails() {
      this.resetAccountDetails();
      this.isLoading = true;
      const { getAccountDetails } = useAccountsApi();

      const data: AccountModel = await getAccountDetails();

      this.id = data.Id || '';
      this.identity = data.Identity || {};
      this.status = data.Status || '';
      this.accountBalance = data.AccountBalance ?? null;
      this.type = data.Type || '';
      this.documents = data.Documents || [];
      this.profiles = data.Profiles || [];
      this.requirements = data.Requirements || [];
      this.restrictions = data.Restrictions || [];
      this.selectedProfile = this.profiles[this.profiles.length - 1] || {};
      this.selectedRequirement = this.requirements[this.requirements.length - 1] || {};
      this.OnboardingId = data.OnboardingId;
      this.isLoading = false;
      this.hasComplianceReport = data.HasComplianceReport;
    }
  }
});
